// @ts-nocheck
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { goToRecruitLook, RL_COACH_DASHBOARD_PATH } from '@/lib/recruitlookHandoff';
import { useAuth } from '@/contexts/AuthContext';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import EventVerificationNotice from '@/components/events/EventVerificationNotice';

export default function EventRegistrationSuccessV2() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { user } = useAuth();
  
  
  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (sessionId) {
      verifyAndLoadRegistration();
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);

  async function verifyAndLoadRegistration() {
    try {
      setLoading(true);

      // Find ALL registrations by Stripe session ID (may be multiple for multi-team)
      const { data: regs, error: regError } = await (supabase as any)
        .from('registrations')
        .select(`
          *,
          event:events(
            id,
            name,
            slug,
            start_date,
            end_date
          )
        `)
        .eq('stripe_session_id', sessionId);

      if (regError) throw regError;

      if (!regs || regs.length === 0) {
        throw new Error('Registration not found');
      }

      console.log(`[Success] Found ${regs.length} registrations for session`);

      // Store ALL registrations for display
      setRegistrations(regs);

      const processedKey = `paynow_processed_${sessionId}`;
      const alreadyProcessed = sessionId ? localStorage.getItem(processedKey) === '1' : false;

      // Get all registration IDs for this session
      const allRegistrationIds = regs.map(r => r.id);

      // If any are still pending/checkout_pending, complete ALL of them
      const pendingRegs = regs.filter(r => 
        r.payment_status === 'pending' || 
        r.payment_status === 'checkout_pending' ||
        r.status === 'checkout_pending'
      );

      if (pendingRegs.length > 0) {
        console.log(`[Success] Updating ${pendingRegs.length} pending registrations to paid/confirmed`);

        // Update ALL registrations in this session to paid/confirmed
        const { error: updateError } = await (supabase as any)
          .from('registrations')
          .update({
            payment_status: 'paid',
            status: 'confirmed',
          })
          .eq('stripe_session_id', sessionId);

        if (updateError) {
          console.error('[Success] Error updating registrations:', updateError);
        }
      }

      // Trigger post-payment processing (best-effort) once per session - for ALL registrations
      if (!alreadyProcessed) {
        console.log(`[Success] Calling process-pay-now-completion for ${allRegistrationIds.length} registration(s)...`);
        const { error: processError } = await invokeEdgeFunction('process-pay-now-completion', {
          body: {
            registration_ids: allRegistrationIds, // Pass ALL registration IDs
            session_id: sessionId,
          },
        });

        if (processError) {
          console.error('[Success] process-pay-now-completion error:', processError);
        } else {
          localStorage.setItem(processedKey, '1');
          console.log('[Success] Post-payment processing completed for all registrations');
        }
      }

      // Reload to get updated status for ALL registrations
      const { data: updated } = await (supabase as any)
        .from('registrations')
        .select(`
          *,
          event:events(
            id,
            name,
            slug,
            start_date,
            end_date
          )
        `)
        .eq('stripe_session_id', sessionId);

      if (updated && updated.length > 0) {
        setRegistrations(updated);
      }
    } catch (err: any) {
      console.error('[Success] Error:', err);
      setError(err.message || 'Failed to verify registration');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-2xl">
        <Card className="p-12 text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-500" />
          <h2 className="text-xl font-semibold">Verifying your registration...</h2>
        </Card>
      </div>
    );
  }

  if (error || registrations.length === 0) {
    return (
      <div className="container mx-auto px-4 py-16 max-w-2xl">
        <Card className="p-12 text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
          <p className="text-gray-600 mb-6">
            {error || 'Unable to verify your registration'}
          </p>
          <Button asChild>
            <Link to="/events">Back to Events</Link>
          </Button>
        </Card>
      </div>
    );
  }

  // Use the first registration for event info (all are from same event/session)
  const primaryRegistration = registrations[0];

  // Build list of all participant names
  const participantNames = registrations.map(reg => 
    reg.registrant_data?.athlete?.name ||
    reg.registrant_data?.team?.name ||
    'Participant'
  );

  // Calculate total paid across ALL registrations from stored dollar amounts
  // Convert to cents for display (the formatCurrency below divides by 100)
  const totalPaid = registrations.reduce((sum, reg) => {
    // Stored in DOLLARS, convert to cents
    const regAmountCents = (Number(reg.registration_amount) || 0) * 100;
    const extrasAmountCents = (Number(reg.extras_amount) || 0) * 100;
    const specialRequestCents = (Number(reg.special_request_amount) || 0) * 100;
    const ticketsAmountCents = (Number(reg.tickets_amount) || 0) * 100;
    // Processing fee stored in cents in pricing_breakdown
    const processingFeeCents = Number(reg.pricing_breakdown?.processing_fee) || 0;
    return sum + regAmountCents + extrasAmountCents + specialRequestCents + ticketsAmountCents + processingFeeCents;
  }, 0);

  return (
    <div className="container mx-auto px-4 py-16 max-w-2xl">
      <Card className="p-12">
        <div className="text-center mb-8">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2">Registration Confirmed!</h1>
          <p className="text-gray-600">
            Your payment has been processed successfully
          </p>
        </div>

        {primaryRegistration?.event?.id && (
          <EventVerificationNotice eventId={primaryRegistration.event.id} className="mb-6" />
        )}

        <div className="border-t border-b py-6 mb-6 space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Event</span>
            <span className="font-semibold">{primaryRegistration.event.name}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">{registrations.length > 1 ? 'Participants' : 'Participant'}</span>
            <div className="text-right">
              {participantNames.map((name, idx) => (
                <div key={idx} className="font-semibold">{name}</div>
              ))}
            </div>
          </div>

          <div className="flex justify-between">
            <span className="text-gray-600">Registration Type</span>
            <span className="font-semibold capitalize">
              {primaryRegistration.registration_type}
            </span>
          </div>

          {/* Show roster info for each team registration */}
          {registrations.map((reg, idx) => (
            reg.registrant_data?.team?.roster && (
              <div key={idx} className="flex justify-between">
                <span className="text-gray-600">
                  {registrations.length > 1 ? `${reg.registrant_data?.team?.name || 'Team'} Size` : 'Team Size'}
                </span>
                <span className="font-semibold">
                  {reg.registrant_data.team.roster.length} players
                </span>
              </div>
            )
          ))}

          {/* Entry Fee (Registration Amount) for each team */}
          <div className="flex justify-between">
            <span className="text-gray-600">{registrations.length > 1 ? 'Entry Fees' : 'Entry Fee'}</span>
            <div className="text-right">
              {registrations.map((reg, idx) => {
                const teamName = reg.registrant_data?.team?.name || reg.registrant_data?.athlete?.name || 'Registration';
                const amount = Number(reg.registration_amount) || 0;
                return registrations.length > 1 ? (
                  <div key={idx} className="text-sm">
                    {teamName}: ${amount.toFixed(2)}
                  </div>
                ) : (
                  <span key={idx} className="font-semibold">${amount.toFixed(2)}</span>
                );
              })}
            </div>
          </div>

          {/* Extras from all registrations */}
          {registrations.some(reg => reg.pricing_breakdown?.extras?.length > 0) && (
            <div className="flex justify-between">
              <span className="text-gray-600">Extras Added</span>
              <div className="text-right">
                {registrations.flatMap(reg => 
                  (reg.pricing_breakdown?.extras || []).map((extra: any, idx: number) => (
                    <div key={`${reg.id}-${idx}`} className="text-sm">
                      {extra.name} (+${(extra.price / 100).toFixed(2)})
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Special request fees */}
          {registrations.some(reg => reg.pricing_breakdown?.special_request_fee > 0) && (
            <div className="flex justify-between">
              <span className="text-gray-600">Schedule Request</span>
              <span className="font-semibold">
                ${(registrations.reduce((sum, reg) => sum + (reg.pricing_breakdown?.special_request_fee || 0), 0) / 100).toFixed(2)}
              </span>
            </div>
          )}

          {/* Spectator tickets from all registrations */}
          {registrations.some(reg => reg.pricing_breakdown?.spectator_tickets?.length > 0) && (
            <div className="flex justify-between">
              <span className="text-gray-600">Spectator Tickets</span>
              <div className="text-right">
                {registrations.flatMap(reg => 
                  (reg.pricing_breakdown?.spectator_tickets || []).map((ticket: any, idx: number) => (
                    <div key={`${reg.id}-${idx}`} className="text-sm">
                      {ticket.name} x{ticket.quantity} (${((ticket.price * ticket.quantity) / 100).toFixed(2)})
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Processing fee */}
          {registrations.some(reg => (reg.processing_fee || reg.pricing_breakdown?.processing_fee) > 0) && (
            <div className="flex justify-between text-muted-foreground">
              <span>Processing Fee</span>
              <span>
                ${(
                  registrations.reduce((sum, reg) => {
                    const feeCents = reg.processing_fee ?? reg.pricing_breakdown?.processing_fee ?? 0;
                    return sum + (Number(feeCents) || 0);
                  }, 0) / 100
                ).toFixed(2)}
              </span>
            </div>
          )}

          <div className="flex justify-between">
            <span className="text-gray-600">{registrations.length > 1 ? 'Registration IDs' : 'Registration ID'}</span>
            <div className="text-right">
              {registrations.map((reg, idx) => (
                <div key={idx} className="font-mono text-sm">{reg.id.slice(0, 8)}</div>
              ))}
            </div>
          </div>

          <div className="flex justify-between text-lg font-bold pt-3 border-t">
            <span>Total Paid</span>
            <span>${(totalPaid / 100).toFixed(2)}</span>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6 space-y-3">
          <p className="text-sm text-blue-900 font-semibold text-center">
            Confirmation email sent!
          </p>
          <p className="text-sm text-blue-800 text-center">
            Check your inbox for registration details and next steps.
          </p>
          <p className="text-sm text-blue-700 text-center">
            You can also see all your orders from your RecruitLook Account under <strong>My Digital Wallet</strong>.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          {user && (
            <Button className="w-full" onClick={() => goToRecruitLook(RL_COACH_DASHBOARD_PATH)}>
              Go to your coach dashboard
            </Button>
          )}

          <Button variant={user ? "outline" : "default"} asChild className="w-full">
            <Link to={`/events/${primaryRegistration.event.slug || primaryRegistration.event.id}`}>
              View Event Details
            </Link>
          </Button>
          
          
          <Button variant="outline" asChild className="w-full">
            <Link to="/events">
              Browse More Events
            </Link>
          </Button>
        </div>
      </Card>
    </div>
  );
}